import { useState, useEffect } from "react";

import {
  Text,
  Chip,
  TwSelector,
  Button,
  Icon,
  Dialog,
  HelperText,
} from "@virkesborsen/ui-component-library";
import { useDrone } from "../../../context/DroneContext";

const Card = ({ price }) => {
  return (
    <div className="flex flex-row gap-2 w-full border border-transparent justify-between p-4 rounded-xl bg-cloud-300">
      <div className="flex flex-col">
        <div className="flex flex-row gap-1 items-baseline pb-1">
          <Text variant="labelLargeMedium" className="text-charcoal-500">
            Ortofoto
          </Text>
          <Text variant="labelLargeMedium" className="text-pebble-500">
            Drönaranalys
          </Text>
        </div>
        <Text variant="labelMediumRegular" className="pb-3 text-charcoal-500">
          Högupplösta bilder & skadeanalys
        </Text>

        <Text variant="bodySmallRegular" className="pb-4 text-charcoal-500">
          Exakt överblick över din skog -direkt i din skogsbruksplan.
        </Text>
      </div>

      <div className="flex flex-col h-full justify-between items-end">
        <Chip
          label="Ingår"
          size="medium"
          icon="CheckSimple"
          className="bg-charcoal-500 text-spring-yellow-500 fill-spring-yellow-500"
        />

        {price && (
          <div className="flex flex-col items-end gap-1">
            <div className="flex flex-row gap-1 items-baseline">
              <Text
                variant="displayAndHeadline2XtraSmallMedium"
                className="text-charcoal-500"
              >
                {price}
              </Text>
              <Text variant="labelLargeRegular" className="text-charcoal-500">
                SEK
              </Text>
            </div>
            <Text variant="captionRegular" className="text-pebble-500">
              per hektar
            </Text>
          </div>
        )}
      </div>
    </div>
  );
};

export default function PropertySelect() {
  const {
    userProperties,
    addSelectedProperty,
    selectedProperties,
    removeSelectedProperty,
    errorMessages,
    setErrorMessages,
    orderPrices,
  } = useDrone();

  const [isSelectPropertyOpen, setIsSelectPropertyOpen] = useState(false);

  const totalArea = selectedProperties.reduce(
    (acc, curr) => acc + curr.area,
    0
  );
  const hasSelectedProperties = selectedProperties.length > 0;

  useEffect(() => {
    if (selectedProperties.length > 0) {
      setErrorMessages((prev) => ({ ...prev, properties: undefined }));
    }
  }, [selectedProperties]);

  const handleSelect = (option) => {
    const selectedProperty = userProperties.find(
      (prop) => prop.ref === option.value
    );
    if (!selectedProperties.some((p) => p.ref === selectedProperty.ref)) {
      addSelectedProperty(selectedProperty);
    } else {
      removeSelectedProperty(selectedProperty);
    }
  };

  const handleRemove = (property) => {
    removeSelectedProperty(property);
  };

  return (
    <>
      <div className="w-full flex flex-col gap-1-5">
        <div className="flex flex-col gap-2 mx-4 mb-4">
          <Text variant="labelLargeMedium">Välj Fastighet</Text>
          <Text variant="bodySmallRegular" className="text-pebble-500">
            Lägg till en eller flera fastigheter
          </Text>
        </div>

        <div className="flex flex-col rounded-2xl bg-white-50 p-4 w-full">
          <div
            className={`flex flex-row gap-1 justify-between items-center ${
              hasSelectedProperties ? "border-b border-stone-200 pb-4 mb-4" : ""
            }`}
          >
            <div className="flex flex-col gap-1">
              <Text variant="labelMediumMedium">
                {hasSelectedProperties
                  ? `Valda fastigheter (${selectedProperties.length})`
                  : "Inga fastigheter valda"}
              </Text>
              {hasSelectedProperties && (
                <Text variant="labelMediumRegular" className="text-pebble-500">
                  {totalArea} hektar
                </Text>
              )}
            </div>
            <Button
              tw
              variant="secondary"
              size="small"
              onClick={() => setIsSelectPropertyOpen(true)}
            >
              {hasSelectedProperties ? "Ändra" : "Lägg till"}
            </Button>
          </div>
          <div className="flex flex-col gap-6">
            {selectedProperties.map((p) => (
              <div
                key={p.ref}
                className="flex flex-row gap-1 justify-between items-center"
              >
                <div className="flex flex-col gap-1">
                  <Text variant="labelMediumMedium">{p.designation}</Text>
                  <Text
                    variant="labelMediumRegular"
                    className="text-pebble-500"
                  >
                    {!p.area ? "..." : `${p.area} hektar`}
                  </Text>
                </div>
                <Button
                  tw
                  variant="outline"
                  size="small"
                  onClick={() => handleRemove(p)}
                >
                  Radera
                </Button>
              </div>
            ))}
          </div>
        </div>
        {errorMessages.properties && (
          <HelperText
            icon="ExclamationFilled"
            label={errorMessages.properties}
            className={"text-danger-500 fill-danger-500 pl-4"}
          />
        )}
      </div>

      <div className="w-full h-[0.5px] border border-stone-500/17 my-4" />

      <div className="flex flex-col gap-4 h-full">
        <div className="flex flex-col gap-2 pl-4">
          <Text variant="labelLargeMedium">
            Alla paket börjar med ortofoto.
          </Text>
          <Text variant="bodySmallRegular" className="text-pebble-500">
            För att utöka analysen kan ett av tilläggen väljas till.
          </Text>
        </div>
        <Card price={orderPrices.propertiesHectarePrice} />
      </div>

      {/* Select Property Dialog */}
      <Dialog isOpen={isSelectPropertyOpen} size="medium">
        <Dialog.Header>
          <div className="flex w-full justify-between">
            <Text variant="labelLargeMedium">Välj Fastighet</Text>
            <Button
              tw
              variant="secondary"
              size="icon"
              onClick={() => setIsSelectPropertyOpen(false)}
            >
              <Icon
                tw
                name="CrossSimple"
                className="color-charcoal-500"
                size="medium"
              />
            </Button>
          </div>
        </Dialog.Header>
        <Dialog.Content>
          <TwSelector>
            {userProperties?.map((option) => (
              <TwSelector.Option
                key={option.ref}
                label={option.designation}
                value={option.ref}
                isSelected={selectedProperties?.some(
                  (p) => p.ref === option.ref
                )}
                onClick={handleSelect}
                disabled={option.disabled}
              />
            ))}
          </TwSelector>
        </Dialog.Content>
        <Dialog.Footer>
          <Button
            tw
            variant="primary"
            onClick={() => setIsSelectPropertyOpen(false)}
          >
            Klar
          </Button>
        </Dialog.Footer>
      </Dialog>
    </>
  );
}
